import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, UserIcon, TagIcon, FolderIcon, SearchIcon, FilterIcon, ChevronDownIcon, ChevronUpIcon, ClockIcon, RefreshCwIcon } from 'lucide-react';
import SectionHeading from '../components/SectionHeading';
import { getPublishedArticles, getFeaturedArticles, searchArticles, Article } from '../services/articles';
import { getAllCategories, Category } from '../services/categories';
import EmailSubscribe from '../components/blog/EmailSubscribe';
const ENewsPage: React.FC = () => {
  const [articles, setArticles] = useState<Article[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Search states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [advancedSearchOpen, setAdvancedSearchOpen] = useState(false);
  // Pagination
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [hasMore, setHasMore] = useState(true);
  // Available tags (extracted from articles)
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  useEffect(() => {
    fetchInitialData();
  }, []);
  const fetchInitialData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Fetching initial data for eNews page...');
      // Fetch all data in parallel
      const [articlesData, fetchedFeatured, fetchedCategories] = await Promise.all([getPublishedArticles(10), getFeaturedArticles(3), getAllCategories()]);
      console.log(`Loaded ${articlesData.articles.length} articles, ${fetchedFeatured.length} featured, ${fetchedCategories.length} categories`);
      setArticles(articlesData.articles);
      setLastDoc(articlesData.lastDoc);
      setHasMore(articlesData.articles.length >= 10);
      setFeaturedArticles(fetchedFeatured);
      setCategories(fetchedCategories);
      // Extract unique tags from articles
      const tags = new Set<string>();
      articlesData.articles.forEach(article => {
        if (article.tags) {
          article.tags.forEach(tag => tags.add(tag));
        }
      });
      setAvailableTags(Array.from(tags));
    } catch (err) {
      console.error('Error fetching data:', err);
      setError(`Failed to load articles. Please try again later. ${err instanceof Error ? err.message : ''}`);
    } finally {
      setLoading(false);
    }
  };
  const handleSearch = async () => {
    try {
      setLoading(true);
      setError(null);
      // Convert date strings to Date objects if provided
      const startDateObj = startDate ? new Date(startDate) : undefined;
      const endDateObj = endDate ? new Date(endDate) : undefined;
      // Perform search
      const result = await searchArticles({
        searchTerm,
        categoryId: selectedCategory || undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        startDate: startDateObj,
        endDate: endDateObj,
        limit: 10
      });
      setArticles(result.articles);
      setLastDoc(result.lastDoc);
      setHasMore(result.articles.length >= 10);
    } catch (err) {
      setError('Failed to search articles. Please try again later.');
      console.error('Error searching articles:', err);
    } finally {
      setLoading(false);
    }
  };
  const handleLoadMore = async () => {
    if (!lastDoc || loadingMore) return;
    try {
      setLoadingMore(true);
      // Convert date strings to Date objects if provided
      const startDateObj = startDate ? new Date(startDate) : undefined;
      const endDateObj = endDate ? new Date(endDate) : undefined;
      // Fetch more articles with current filters
      const result = await searchArticles({
        searchTerm,
        categoryId: selectedCategory || undefined,
        tags: selectedTags.length > 0 ? selectedTags : undefined,
        startDate: startDateObj,
        endDate: endDateObj,
        startAfterDoc: lastDoc,
        limit: 10
      });
      setArticles([...articles, ...result.articles]);
      setLastDoc(result.lastDoc);
      setHasMore(result.articles.length >= 10);
    } catch (err) {
      console.error('Error loading more articles:', err);
    } finally {
      setLoadingMore(false);
    }
  };
  const handleTagToggle = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };
  const handleResetFilters = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setSelectedTags([]);
    setStartDate('');
    setEndDate('');
    fetchInitialData();
  };
  return <div className="w-full bg-lightGray" data-id="element-1095">
      <section className="py-12 bg-navy text-white" data-id="element-1096">
        <div className="container-custom" data-id="element-1097">
          <h1 className="text-4xl font-bold font-montserrat mb-4" data-id="element-1098">
            Operation Launchpad eNews
          </h1>
          <p className="text-xl max-w-3xl" data-id="element-1099">
            Stay updated with the latest entrepreneurial insights, success
            stories, and program updates.
          </p>
          <div className="mt-4" data-id="element-1100">
            <Link to="/rss" className="text-gold hover:underline flex items-center w-fit" data-id="element-1101">
              <RSSIcon className="mr-2" data-id="element-1102" />
              Subscribe to RSS Feed
            </Link>
          </div>
        </div>
      </section>

      {featuredArticles.length > 0 && <section className="py-12 bg-white" data-id="element-1103">
          <div className="container-custom" data-id="element-1104">
            <h2 className="text-2xl font-bold text-navy mb-8 font-montserrat" data-id="element-1105">
              Featured Articles
            </h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-1106">
              {featuredArticles.map((article, index) => <FeaturedArticleCard key={article.id} article={article} isMain={index === 0} data-id="element-1107" />)}
            </div>
          </div>
        </section>}

      <section className="section-padding" data-id="element-1108">
        <div className="container-custom" data-id="element-1109">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8" data-id="element-1110">
            <div className="lg:col-span-3" data-id="element-1111">
              <div className="bg-white p-6 rounded-lg shadow-sm mb-8" data-id="element-1112">
                <div className="flex flex-col gap-4" data-id="element-1113">
                  {/* Basic Search */}
                  <div className="flex flex-col md:flex-row gap-4" data-id="element-1114">
                    <div className="flex-1" data-id="element-1115">
                      <div className="relative" data-id="element-1116">
                        <input type="text" placeholder="Search articles..." className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSearch()} data-id="element-1117" />
                        <SearchIcon size={18} className="absolute left-3 top-2.5 text-gray-400" data-id="element-1118" />
                      </div>
                    </div>
                    <div className="w-full md:w-48" data-id="element-1119">
                      <select className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange appearance-none" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} data-id="element-1120">
                        <option value="" data-id="element-1121">All Categories</option>
                        {categories.map(category => <option key={category.id} value={category.id} data-id="element-1122">
                            {category.name}
                          </option>)}
                      </select>
                    </div>
                    <div className="w-full md:w-auto" data-id="element-1123">
                      <button onClick={handleSearch} className="w-full md:w-auto bg-navy text-white px-4 py-2 rounded-md hover:bg-navy-800 transition duration-300" data-id="element-1124">
                        Search
                      </button>
                    </div>
                  </div>

                  {/* Advanced Search Toggle */}
                  <div className="flex justify-between items-center" data-id="element-1125">
                    <button onClick={() => setAdvancedSearchOpen(!advancedSearchOpen)} className="text-navy flex items-center text-sm" data-id="element-1126">
                      {advancedSearchOpen ? <>
                          <ChevronUpIcon size={16} className="mr-1" data-id="element-1127" />
                          Hide Advanced Search
                        </> : <>
                          <ChevronDownIcon size={16} className="mr-1" data-id="element-1128" />
                          Show Advanced Search
                        </>}
                    </button>
                    {(searchTerm || selectedCategory || selectedTags.length > 0 || startDate || endDate) && <button onClick={handleResetFilters} className="text-gray-600 flex items-center text-sm" data-id="element-1129">
                        <RefreshCwIcon size={14} className="mr-1" data-id="element-1130" />
                        Reset Filters
                      </button>}
                  </div>

                  {/* Advanced Search */}
                  {advancedSearchOpen && <div className="pt-4 border-t border-gray-200 mt-2" data-id="element-1131">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-1132">
                        <div data-id="element-1133">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1134">
                            From Date
                          </label>
                          <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" value={startDate} onChange={e => setStartDate(e.target.value)} data-id="element-1135" />
                        </div>
                        <div data-id="element-1136">
                          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1137">
                            To Date
                          </label>
                          <input type="date" className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange" value={endDate} onChange={e => setEndDate(e.target.value)} data-id="element-1138" />
                        </div>
                      </div>
                      <div className="mb-4" data-id="element-1139">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1140">
                          Tags
                        </label>
                        <div className="flex flex-wrap gap-2" data-id="element-1141">
                          {availableTags.map(tag => <button key={tag} onClick={() => handleTagToggle(tag)} className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${selectedTags.includes(tag) ? 'bg-orange text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`} data-id="element-1142">
                              <TagIcon size={12} className="mr-1" data-id="element-1143" />
                              {tag}
                            </button>)}
                        </div>
                      </div>
                    </div>}
                </div>
              </div>

              {loading ? <div className="flex justify-center py-12" data-id="element-1144">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-navy" data-id="element-1145"></div>
                </div> : error ? <div className="text-center py-8 bg-white rounded-lg shadow-sm" data-id="element-1146">
                  <div className="bg-red-50 text-red-700 p-6 rounded-md mb-4 text-left" data-id="element-1147">
                    <h3 className="font-bold mb-2 text-lg" data-id="element-1148">
                      Error Loading Articles
                    </h3>
                    <p className="mb-4" data-id="element-1149">{error}</p>
                    <p className="text-sm mb-4" data-id="element-1150">
                      This may be due to missing Firestore indexes or
                      configuration issues.
                    </p>
                  </div>
                  <button onClick={fetchInitialData} className="px-4 py-2 bg-navy text-white rounded-md hover:bg-navy-800 flex items-center mx-auto" data-id="element-1151">
                    <RefreshCwIcon size={16} className="mr-2" data-id="element-1152" />
                    Try Again
                  </button>
                </div> : articles.length === 0 ? <div className="text-center py-12 bg-white rounded-lg shadow-sm" data-id="element-1153">
                  <p className="text-xl" data-id="element-1154">
                    {searchTerm || selectedCategory || selectedTags.length > 0 || startDate || endDate ? 'No articles match your search criteria.' : 'No articles published yet. Check back soon!'}
                  </p>
                </div> : <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1155">
                    {articles.map(article => <ArticleCard key={article.id} article={article} data-id="element-1156" />)}
                  </div>
                  {hasMore && <div className="mt-8 text-center" data-id="element-1157">
                      <button onClick={handleLoadMore} disabled={loadingMore} className="px-6 py-2 bg-navy text-white rounded-md hover:bg-navy-800 transition duration-300" data-id="element-1158">
                        {loadingMore ? <span className="flex items-center" data-id="element-1159">
                            <span className="animate-spin h-4 w-4 mr-2 border-t-2 border-b-2 border-white rounded-full" data-id="element-1160"></span>
                            Loading...
                          </span> : 'Load More Articles'}
                      </button>
                    </div>}
                </>}
            </div>
            <div className="lg:col-span-1" data-id="element-1161">
              <div className="sticky top-24" data-id="element-1162">
                <EmailSubscribe data-id="element-1163" />
                {categories.length > 0 && <div className="mt-8 bg-white p-6 rounded-lg shadow-sm" data-id="element-1164">
                    <h3 className="text-xl font-bold text-navy mb-4 font-montserrat" data-id="element-1165">
                      Categories
                    </h3>
                    <ul className="space-y-2" data-id="element-1166">
                      {categories.map(category => <li key={category.id} data-id="element-1167">
                          <button onClick={() => {
                      setSelectedCategory(category.id || '');
                      handleSearch();
                    }} className="flex items-center text-navy hover:text-orange transition duration-300 w-full text-left" data-id="element-1168">
                            <FolderIcon size={16} className="mr-2" data-id="element-1169" />
                            {category.name}
                          </button>
                        </li>)}
                    </ul>
                  </div>}
                <div className="mt-8 bg-white p-6 rounded-lg shadow-sm" data-id="element-1170">
                  <h3 className="text-xl font-bold text-navy mb-4 font-montserrat" data-id="element-1171">
                    RSS Feed
                  </h3>
                  <p className="text-gray-600 mb-4" data-id="element-1172">
                    Subscribe to our RSS feed to get the latest articles
                    delivered to your feed reader.
                  </p>
                  <Link to="/rss" className="inline-flex items-center text-orange hover:underline" data-id="element-1173">
                    <RSSIcon className="mr-2" data-id="element-1174" />
                    View RSS Feed
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
};
interface ArticleCardProps {
  article: Article;
}
const ArticleCard: React.FC<ArticleCardProps> = ({
  article
}) => {
  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Date unavailable';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  return <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:shadow-lg hover:-translate-y-1" data-id="element-1175">
      {article.featuredImage && <div className="h-48 overflow-hidden" data-id="element-1176">
          <img src={article.featuredImage} alt={article.title} className="w-full h-full object-cover" data-id="element-1177" />
        </div>}
      <div className="p-6" data-id="element-1178">
        <h3 className="text-xl font-bold text-navy mb-3 font-montserrat" data-id="element-1179">
          <Link to={`/enews/${article.id}`} className="hover:text-orange transition duration-300" data-id="element-1180">
            {article.title}
          </Link>
        </h3>
        <p className="text-darkGray mb-4 line-clamp-3" data-id="element-1181">{article.excerpt}</p>
        <div className="flex flex-wrap items-center text-sm text-gray-500 mb-4 gap-4" data-id="element-1182">
          <div className="flex items-center" data-id="element-1183">
            <CalendarIcon size={16} className="mr-1" data-id="element-1184" />
            <span data-id="element-1185">{formatDate(article.publishedAt)}</span>
          </div>
          <div className="flex items-center" data-id="element-1186">
            <UserIcon size={16} className="mr-1" data-id="element-1187" />
            <span data-id="element-1188">{article.author.name}</span>
          </div>
          {article.readTime && <div className="flex items-center" data-id="element-1189">
              <ClockIcon size={16} className="mr-1" data-id="element-1190" />
              <span data-id="element-1191">{article.readTime} min read</span>
            </div>}
          {article.categoryName && <div className="flex items-center" data-id="element-1192">
              <FolderIcon size={16} className="mr-1" data-id="element-1193" />
              <Link to={`/enews?category=${article.categoryId}`} className="hover:text-orange transition duration-300" data-id="element-1194">
                {article.categoryName}
              </Link>
            </div>}
        </div>
        {article.tags && article.tags.length > 0 && <div className="flex flex-wrap gap-2 mt-3" data-id="element-1195">
            {article.tags.slice(0, 3).map((tag, index) => <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-1196">
                <TagIcon size={12} className="mr-1" data-id="element-1197" />
                {tag}
              </span>)}
          </div>}
        <div className="mt-4" data-id="element-1198">
          <Link to={`/enews/${article.id}`} className="text-navy font-medium hover:text-orange transition duration-300" data-id="element-1199">
            Read More →
          </Link>
        </div>
      </div>
    </div>;
};
interface FeaturedArticleCardProps {
  article: Article;
  isMain?: boolean;
}
const FeaturedArticleCard: React.FC<FeaturedArticleCardProps> = ({
  article,
  isMain = false
}) => {
  // Format date
  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Date unavailable';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  return <div className={`bg-white rounded-lg shadow-md overflow-hidden transition-transform duration-300 hover:shadow-lg hover:-translate-y-1 ${isMain ? 'lg:col-span-3 md:col-span-2' : ''}`} data-id="element-1200">
      <div className="relative" data-id="element-1201">
        {article.featuredImage ? <div className={`${isMain ? 'h-72' : 'h-48'} overflow-hidden`} data-id="element-1202">
            <img src={article.featuredImage} alt={article.title} className="w-full h-full object-cover" data-id="element-1203" />
          </div> : <div className={`${isMain ? 'h-72' : 'h-48'} bg-gray-200 flex items-center justify-center`} data-id="element-1204">
            <span className="text-gray-400" data-id="element-1205">No image available</span>
          </div>}
        <div className="absolute top-4 left-4" data-id="element-1206">
          <span className="bg-gold text-navy px-3 py-1 rounded-full text-sm font-bold" data-id="element-1207">
            Featured
          </span>
        </div>
      </div>
      <div className="p-6" data-id="element-1208">
        <h3 className={`${isMain ? 'text-2xl' : 'text-xl'} font-bold text-navy mb-3 font-montserrat`} data-id="element-1209">
          <Link to={`/enews/${article.id}`} className="hover:text-orange transition duration-300" data-id="element-1210">
            {article.title}
          </Link>
        </h3>
        <p className="text-darkGray mb-4 line-clamp-3" data-id="element-1211">{article.excerpt}</p>
        <div className="flex flex-wrap items-center text-sm text-gray-500 mb-4 gap-4" data-id="element-1212">
          <div className="flex items-center" data-id="element-1213">
            <CalendarIcon size={16} className="mr-1" data-id="element-1214" />
            <span data-id="element-1215">{formatDate(article.publishedAt)}</span>
          </div>
          <div className="flex items-center" data-id="element-1216">
            <UserIcon size={16} className="mr-1" data-id="element-1217" />
            <span data-id="element-1218">{article.author.name}</span>
          </div>
          {article.readTime && <div className="flex items-center" data-id="element-1219">
              <ClockIcon size={16} className="mr-1" data-id="element-1220" />
              <span data-id="element-1221">{article.readTime} min read</span>
            </div>}
          {article.categoryName && <div className="flex items-center" data-id="element-1222">
              <FolderIcon size={16} className="mr-1" data-id="element-1223" />
              <Link to={`/enews?category=${article.categoryId}`} className="hover:text-orange transition duration-300" data-id="element-1224">
                {article.categoryName}
              </Link>
            </div>}
        </div>
        <div className="mt-4" data-id="element-1225">
          <Link to={`/enews/${article.id}`} className="text-navy font-medium hover:text-orange transition duration-300" data-id="element-1226">
            Read More →
          </Link>
        </div>
      </div>
    </div>;
};
// RSS Icon component
const RSSIcon = ({
  className = ''
}) => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-1227">
    <path d="M4 11a9 9 0 0 1 9 9" data-id="element-1228" />
    <path d="M4 4a16 16 0 0 1 16 16" data-id="element-1229" />
    <circle cx="5" cy="19" r="1" data-id="element-1230" />
  </svg>;
export default ENewsPage;